export function escape(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]!));
}

export function query<T extends Element = HTMLElement>(selector: string, scope: ParentNode = document): T {
  const node = scope.querySelector<T>(selector);
  if (!node) throw new Error(`Missing interface element: ${selector}`);
  return node;
}

export function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const result = document.createElement('button'); result.type = 'button'; result.className = className;
  result.textContent = label; result.addEventListener('click', onClick); return result;
}

export function saved<T>(key: string, fallback: T): T {
  try { const text = localStorage.getItem(key); return text === null ? fallback : JSON.parse(text) as T; } catch { return fallback; }
}
export function save(key: string, value: unknown) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Preferences are optional. */ } }
export function timeLabel(timestamp: number) { return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
export function dateLabel(timestamp: number) { return new Date(timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }); }
export function countdown(milliseconds: number) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(seconds / 3600), minutes = Math.floor(seconds % 3600 / 60);
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}m` : minutes > 0 ? `${minutes}m ${String(seconds % 60).padStart(2, '0')}s` : `${seconds}s`;
}

export function announce(message: string, kind: 'success' | 'error' | 'info' = 'info') {
  const region = document.getElementById('toasts');
  if (!region) return;
  const toast = document.createElement('div'); toast.className = `toast toast-${kind}`;
  toast.setAttribute('role', kind === 'error' ? 'alert' : 'status'); toast.textContent = message;
  region.append(toast);
  while (region.children.length > 3) region.firstElementChild?.remove();
  setTimeout(() => toast.remove(), kind === 'error' ? 8000 : 4800);
}

let currentDialog: DialogHandle | null = null;
export function dismissDialog() { currentDialog?.close(); }
export type DialogHandle = { element: HTMLDialogElement; body: HTMLDivElement; close: () => void; closed: boolean };
export function openDialog(title: string, content: string, options: { className?: string; onClose?: () => void; testId?: string } = {}): DialogHandle {
  currentDialog?.close();
  const previousFocus = document.activeElement as HTMLElement | null;
  const element = document.createElement('dialog');
  element.className = `modal ${options.className ?? ''}`;
  if (options.testId) element.dataset.testid = options.testId;
  const labelId = `dialog-${crypto.randomUUID()}`;
  element.setAttribute('aria-labelledby', labelId);
  element.innerHTML = `<header class="modal-header"><h2 id="${labelId}">${escape(title)}</h2><button type="button" class="icon-button modal-close" aria-label="Close dialog" data-testid="dialog-close"><span aria-hidden="true">×</span></button></header><div class="modal-body">${content}</div>`;
  const handle: DialogHandle = { element, body: query<HTMLDivElement>('.modal-body', element), closed: false, close: () => {
    if (handle.closed) return;
    handle.closed = true;
    if (currentDialog === handle) currentDialog = null;
    element.close(); element.remove(); options.onClose?.();
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  } };
  currentDialog = handle;
  query('.modal-close', element).addEventListener('click', handle.close);
  element.addEventListener('cancel', event => { event.preventDefault(); handle.close(); });
  element.addEventListener('keydown', event => {
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const focusable = [...element.querySelectorAll<HTMLElement>('button,a[href],input,textarea,select,[tabindex]')].filter(node =>
      node.tabIndex >= 0 && !node.matches(':disabled') && !node.closest('[inert]') && node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden'
    );
    const first = focusable[0], last = focusable.at(-1);
    if (!first) { event.preventDefault(); element.focus({ preventScroll: true }); return; }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !focusable.includes(active as HTMLElement))) {
      event.preventDefault(); last!.focus({ preventScroll: true });
    } else if (!event.shiftKey && (active === last || !focusable.includes(active as HTMLElement))) {
      event.preventDefault(); first.focus({ preventScroll: true });
    }
  });
  element.addEventListener('click', event => { if (event.target === element) { const bounds = element.getBoundingClientRect(); const mouse = event as MouseEvent; if (mouse.clientX < bounds.left || mouse.clientX > bounds.right || mouse.clientY < bounds.top || mouse.clientY > bounds.bottom) handle.close(); } });
  document.body.append(element); element.showModal();
  const autofocus = element.querySelector<HTMLElement>('[autofocus]');
  if (autofocus) requestAnimationFrame(() => { if (!handle.closed) autofocus.focus({ preventScroll: true }); });
  return handle;
}

export async function copyText(value: string, input?: HTMLInputElement) {
  try {
    if (!navigator.clipboard) throw new Error('Clipboard unavailable');
    await navigator.clipboard.writeText(value); return true;
  } catch {
    input?.focus(); input?.select();
    announce('Select and copy the text. Your browser could not copy it automatically.', 'info'); return false;
  }
}

